"use client";

import { createContext, useContext, useEffect, useMemo, useState } from "react";

export const SUPPORTED_LOCALES = ["de", "en"];
export const DEFAULT_LOCALE = "de";

const translations = {
  de: {
    "nav.dashboard": "Dashboard",
    "nav.production": "Produktion",
    "nav.shipping": "Versand",
    "nav.inventory": "Bestand",
    "nav.admin": "Verwaltung",
    "nav.notifications": "Benachrichtigungen",
    "nav.open": "Navigation öffnen",
    "nav.home": "Startseite",
    "settings.title": "Einstellungen",
    "settings.shippingTitle": "Versand-Arbeitsaufträge",
    "settings.shippingDescription": "Wähle, welche Arbeitsschritte in der Shipping-Worklist bearbeitet werden.",
    "settings.shippingQa": "QA",
    "settings.shippingQaDescription": "Druckmenge auf OK, B-Ware und NOK verteilen.",
    "settings.shippingLabel": "Label",
    "settings.shippingLabelDescription": "Label-URL anzeigen und QR-Code erzeugen.",
    "settings.shippingAtLeastOne": "Mindestens ein Arbeitsschritt muss aktiv bleiben.",
    "settings.shippingSaved": "Versand-Arbeitsschritte gespeichert.",
    "settings.shippingLoading": "Lade Versand-Einstellungen …",
    "settings.description": "Profil, Design und Benachrichtigungen verwalten.",
    "settings.profile": "Profil",
    "settings.appearance": "Design",
    "settings.notifications": "Benachrichtigungen",
    "appearance.title": "Design",
    "appearance.description": "Farben, Theme und UI-Optionen",
    "appearance.language": "Sprache",
    "appearance.languageDescription": "Wähle die Sprache der Benutzeroberfläche.",
    "language.de": "Deutsch",
    "language.en": "English",
    "login.sessionTimeout": "Deine Sitzung wurde nach 30 Minuten Inaktivität beendet.",
    "login.sessionExpired": "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.",
    "login.checking": "Sitzung wird geprüft …",
    "login.checkingSession": "Sitzung wird geprüft …",
    "login.signIn": "Mit Authentik anmelden",
    "login.oidc": "OIDC bereitgestellt durch Authentik",
    "common.show": "Anzeigen",
    "common.hide": "Ausblenden",
    "common.details": "Details",
    "common.cancel": "Abbrechen",
    "common.close": "Schließen",
    "common.delete": "Löschen",
    "common.edit": "Bearbeiten",
    "common.save": "Speichern",
    "common.saving": "Speichere …",
    "common.saved": "Gespeichert",
    "common.loading": "Lade …",
    "common.unknown": "Unbekannt",
    "common.status": "Status",
    "common.actions": "Aktionen",
    "common.allStatuses": "Alle Status",
    "common.searchOrderProduct": "Suche: Auftrag-ID, Produkt …",
    "common.noOrders": "Keine Aufträge vorhanden.",
    "common.resetFilters": "Filter zurücksetzen",
    "common.noProductionOrders": "Noch keine Produktionsaufträge vorhanden.",
    "production.title": "Produktion",
    "production.subtitle": "Produktionsaufträge und Druckjobs verwalten",
    "production.loading": "Lade Produktionsaufträge …",
    "shipping.title": "Versand",
    "shipping.subtitle": "Worklist für QA und Label-Erzeugung",
    "shipping.loading": "Lade Versandaufträge …",
    "shipping.worklist": "Worklist",
    "shipping.worklistDescription": "Arbeitsvorrat nach Charge und nächster Aktion",
    "shipping.entries": "Einträge",
    "shipping.noMatchingLots": "Keine passenden Chargen gefunden.",
    "shipping.selectWorkItem": "Wähle links einen Auftrag oder eine Charge aus.",
    "shipping.order": "Auftrag",
    "shipping.lot": "Charge",
    "shipping.lotOf": "von",
    "shipping.lots": "Chargen",
    "shipping.openLots": "offene Chargen",
    "shipping.workflowPerLot": "Workflow je Charge",
    "shipping.hoverLots": "Chargen anzeigen",
    "shipping.printJobs": "Druckjobs",
    "shipping.quantity": "Menge",
    "shipping.printer": "Drucker",
    "shipping.printedAt": "Gedruckt am",
    "shipping.printPreparation": "Druck wird vorbereitet",
    "shipping.waitingForLot": "Wartet auf Chargenerzeugung",
    "shipping.noLotDetail": "Für diesen Auftrag ist aktuell noch keine Charge verfügbar. Die Detailansicht bleibt read-only, bis ein Druckjob gestartet wurde.",
    "shipping.noLotYet": "Noch keine Charge angelegt",
    "shipping.actionQa": "QA erforderlich",
    "shipping.actionLabel": "Label erforderlich",
    "shipping.actionOdoo": "An Odoo melden",
    "shipping.reprintLabel": "Label nachdrucken",
    "shipping.reportOdoo": "An Odoo melden",
    "shipping.adjustQa": "QA anpassen",
    "shipping.reportedToOdoo": "An Odoo gemeldet",
    "shipping.reportOdooDescription": "Melde die OK-Menge dieser Charge an einen bestehenden Odoo-Auftrag oder lege dafür einen neuen Auftrag an.",
    "shipping.charge": "Charge",
    "shipping.existingOdooOrder": "Bestehender Odoo-Produktionsauftrag",
    "shipping.noOdooOrders": "Kein passender Odoo-Auftrag gefunden",
    "shipping.createNewOdooOrder": "Neuen Odoo-Produktionsauftrag anlegen",
    "shipping.loadingOdooOrders": "Odoo-Aufträge werden geladen …",
    "shipping.noBackorderNotice": "Kein Backorder: Odoo legt für die Restmenge keinen Folgeauftrag an.",
    "shipping.actionPrinting": "Druck läuft",
    "shipping.noOpenAction": "Keine offene Aktion",
    "shipping.noAction": "Keine Aktion erforderlich",
    "shipping.step.print": "Druck",
    "shipping.step.qa": "QA",
    "shipping.step.label": "Label",
    "shipping.qualityCheck": "Qualitätsprüfung",
    "shipping.qualityCheckDescription": "Verteile die vollständige Druckmenge auf OK, B-Ware und NOK.",
    "shipping.createLabel": "Label erzeugen",
    "shipping.createLabelDescription": "Die QA ist abgeschlossen. Erzeuge jetzt die Label-URL und den QR-Code.",
    "shipping.printInProgressNotice": "Der Druck läuft. QA und Label werden nach Abschluss des Druckjobs freigeschaltet.",
    "shipping.workflowCompleteNotice": "Das Label wurde erzeugt. Alle aktuell konfigurierten Arbeitsschritte sind abgeschlossen.",
    "shipping.qaSplit": "QA-Aufteilung",
    "shipping.qaTitle": "QA",
    "shipping.size": "Größe",
    "shipping.printQuantity": "Druckmenge",
    "shipping.notes": "Notizen …",
    "shipping.finishQa": "QA abschließen",
    "shipping.sortNewest": "Zuletzt gedruckt zuerst",
    "shipping.sortOldest": "Älteste Drucke zuerst",
    "shipping.sortLot": "Chargennummer absteigend",
    "shipping.sortProduct": "Produktname A–Z",
    "shipping.statusFilter": "Status",
    "shipping.openOnly": "Nur offene",
    "shipping.labelInformation": "Label-Information",
    "shipping.search": "Auftrag, Produkt oder Charge suchen …",
    "dashboard.activePrinters": "Aktive Drucker",
    "dashboard.printerManagement": "Druckerverwaltung",
    "dashboard.production": "Produktion",
    "dashboard.openProductionOrders": "offene Produktionsaufträge",
    "dashboard.shipping": "Versand",
    "dashboard.openShippingOrders": "offene Versandaufträge",
    "dashboard.batchesToday": "Batches heute",
    "dashboard.productsInStock": "Produkte im Lager",
    "dashboard.newNotification": "Neue Benachrichtigung",
    "dashboard.productionActivity": "Produktion",
    "dashboard.shippingActivity": "Versand",
    "dashboard.batchActivity": "Batch",
    "production.newPrintJob": "Neuer Druckauftrag",
    "production.printJobs": "Druckaufträge",
    "production.startDirect": "Direkt starten",
    "production.queue": "In Queue legen",
    "production.startManual": "Manuell starten",
    "production.completeManual": "Manuell abschließen",
    "production.cancelJob": "Abbrechen",
    "production.turnPrinterOn": "Drucker einschalten",
    "production.labelReprint": "Label neu drucken",
    "shipping.completeOrder": "Auftrag abschließen",
    "shipping.startQa": "QA starten",
    "shipping.printLabel": "Label drucken",
    "shipping.startPacking": "Verpackung starten",
    "shipping.finishPacking": "Verpackung abschließen",
    "shipping.label": "Label",
    "status.CREATED": "Erfasst",
    "status.PLANNED": "Geplant",
    "status.IN_PROGRESS": "Im Druck",
    "status.PROCESSING": "In Nachbearbeitung",
    "status.DONE": "Erfüllt",
    "status.STOPPED": "Gestoppt",
    "status.PROBLEM": "Problem",
    "status.WAITING_FOR_QA": "Wartet auf QA",
    "status.QA_RUNNING": "QA läuft",
    "status.PACKING_RUNNING": "In Verpackung",
    "status.PACKING": "In Verpackung",
    "status.PACKED": "Verpackt/Abgeschlossen",
    "status.COMPLETED": "Abgeschlossen",
    "status.PRODUCED": "Produziert",
    "status.DRAFT": "Entwurf",
    "status.QUEUED": "In Queue",
    "status.PRINTING": "Im Druck",
    "status.CANCELLED": "Abgebrochen",
    "status.PRINTED": "Gedruckt",
    "status.QA_REVIEW": "QA-Prüfung",
    "status.QA_APPROVED": "QA OK",
    "status.LABEL_PRINTED": "Label gedruckt",
    "status.SCRAP": "Ausschuss",
    "status.ACTIVE": "Aktiv",
    "status.INACTIVE": "Inaktiv",
    "status.UNKNOWN": "Unbekannt",
  },
  en: {
    "nav.dashboard": "Dashboard",
    "nav.production": "Production",
    "nav.shipping": "Shipping",
    "nav.inventory": "Inventory",
    "nav.admin": "Administration",
    "nav.notifications": "Notifications",
    "nav.open": "Open navigation",
    "nav.home": "Home",
    "settings.title": "Settings",
    "settings.shippingTitle": "Shipping work steps",
    "settings.shippingDescription": "Choose which work steps are handled in the Shipping worklist.",
    "settings.shippingQa": "QA",
    "settings.shippingQaDescription": "Distribute the print quantity between OK, B-grade and NOK.",
    "settings.shippingLabel": "Label",
    "settings.shippingLabelDescription": "Show the label URL and generate the QR code.",
    "settings.shippingAtLeastOne": "At least one work step must remain active.",
    "settings.shippingSaved": "Shipping work steps saved.",
    "settings.shippingLoading": "Loading shipping settings …",
    "settings.description": "Manage your profile, appearance and notifications.",
    "settings.profile": "Profile",
    "settings.appearance": "Appearance",
    "settings.notifications": "Notifications",
    "appearance.title": "Appearance",
    "appearance.description": "Colors, theme and UI options",
    "appearance.language": "Language",
    "appearance.languageDescription": "Choose the interface language.",
    "language.de": "Deutsch",
    "language.en": "English",
    "login.sessionTimeout": "Your session ended after 30 minutes of inactivity.",
    "login.sessionExpired": "Your session has expired. Please sign in again.",
    "login.checking": "Checking session…",
    "login.checkingSession": "Checking session…",
    "login.signIn": "Sign in with Authentik",
    "login.oidc": "OIDC powered by Authentik",
    "common.show": "Show",
    "common.hide": "Hide",
    "common.details": "Details",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.save": "Save",
    "common.saving": "Saving …",
    "common.saved": "Saved",
    "common.loading": "Loading …",
    "common.unknown": "Unknown",
    "common.status": "Status",
    "common.actions": "Actions",
    "common.allStatuses": "All statuses",
    "common.searchOrderProduct": "Search: order ID, product …",
    "common.noOrders": "No orders available.",
    "common.resetFilters": "Reset filters",
    "common.noProductionOrders": "No production orders available yet.",
    "production.title": "Production",
    "production.subtitle": "Manage production orders and print jobs",
    "production.loading": "Loading production orders …",
    "shipping.title": "Shipping",
    "shipping.subtitle": "Worklist for QA and label generation",
    "shipping.loading": "Loading shipping orders …",
    "shipping.worklist": "Worklist",
    "shipping.worklistDescription": "Work queue by charge and next action",
    "shipping.entries": "entries",
    "shipping.noMatchingLots": "No matching charges found.",
    "shipping.selectWorkItem": "Select an order or charge on the left.",
    "shipping.order": "Order",
    "shipping.lot": "Charge",
    "shipping.lotOf": "of",
    "shipping.lots": "charges",
    "shipping.openLots": "open charges",
    "shipping.workflowPerLot": "Workflow per charge",
    "shipping.hoverLots": "show charges",
    "shipping.printJobs": "Print jobs",
    "shipping.quantity": "Quantity",
    "shipping.printer": "Printer",
    "shipping.printedAt": "Printed",
    "shipping.printPreparation": "Print is being prepared",
    "shipping.waitingForLot": "Waiting for charge creation",
    "shipping.noLotDetail": "No charge is available for this order yet. The detail view remains read-only until a print job is started.",
    "shipping.noLotYet": "No charge created yet",
    "shipping.actionQa": "QA required",
    "shipping.actionLabel": "Label required",
    "shipping.actionOdoo": "Report to Odoo",
    "shipping.reprintLabel": "Reprint label",
    "shipping.reportOdoo": "Report to Odoo",
    "shipping.adjustQa": "Adjust QA",
    "shipping.reportedToOdoo": "Reported to Odoo",
    "shipping.reportOdooDescription": "Report this charge's OK quantity to an existing Odoo order or create a new order.",
    "shipping.charge": "Charge",
    "shipping.existingOdooOrder": "Existing Odoo manufacturing order",
    "shipping.noOdooOrders": "No matching Odoo order found",
    "shipping.createNewOdooOrder": "Create a new Odoo manufacturing order",
    "shipping.loadingOdooOrders": "Loading Odoo orders …",
    "shipping.noBackorderNotice": "No backorder: Odoo will not create a follow-up order for the remainder.",
    "shipping.actionPrinting": "Printing",
    "shipping.noOpenAction": "No open action",
    "shipping.noAction": "No action required",
    "shipping.step.print": "Print",
    "shipping.step.qa": "QA",
    "shipping.step.label": "Label",
    "shipping.qualityCheck": "Quality check",
    "shipping.qualityCheckDescription": "Distribute the complete print quantity between OK, B-grade and NOK.",
    "shipping.createLabel": "Create label",
    "shipping.createLabelDescription": "QA is complete. Create the label URL and QR code.",
    "shipping.printInProgressNotice": "Printing is in progress. QA and label generation become available after printing is complete.",
    "shipping.workflowCompleteNotice": "The label was created. All currently configured work steps are complete.",
    "shipping.qaSplit": "QA split",
    "shipping.qaTitle": "QA",
    "shipping.size": "Size",
    "shipping.printQuantity": "Print quantity",
    "shipping.notes": "Notes …",
    "shipping.finishQa": "Complete QA",
    "shipping.sortNewest": "Newest prints first",
    "shipping.sortOldest": "Oldest prints first",
    "shipping.sortLot": "Charge number descending",
    "shipping.sortProduct": "Product name A–Z",
    "shipping.statusFilter": "Status",
    "shipping.openOnly": "Open only",
    "shipping.labelInformation": "Label information",
    "shipping.search": "Search order, product or charge …",
    "dashboard.activePrinters": "Active printers",
    "dashboard.printerManagement": "Printer management",
    "dashboard.production": "Production",
    "dashboard.openProductionOrders": "open production orders",
    "dashboard.shipping": "Shipping",
    "dashboard.openShippingOrders": "open shipping orders",
    "dashboard.batchesToday": "Batches today",
    "dashboard.productsInStock": "products in stock",
    "dashboard.newNotification": "New notification",
    "dashboard.productionActivity": "Production",
    "dashboard.shippingActivity": "Shipping",
    "dashboard.batchActivity": "Batch",
    "production.newPrintJob": "New print job",
    "production.printJobs": "Print jobs",
    "production.startDirect": "Start directly",
    "production.queue": "Add to queue",
    "production.startManual": "Start manually",
    "production.completeManual": "Complete manually",
    "production.cancelJob": "Cancel",
    "production.turnPrinterOn": "Turn printer on",
    "production.labelReprint": "Reprint label",
    "shipping.completeOrder": "Complete order",
    "shipping.startQa": "Start QA",
    "shipping.printLabel": "Print label",
    "shipping.startPacking": "Start packing",
    "shipping.finishPacking": "Finish packing",
    "shipping.label": "Label",
    "status.CREATED": "Created",
    "status.PLANNED": "Planned",
    "status.IN_PROGRESS": "Printing",
    "status.PROCESSING": "Post-processing",
    "status.DONE": "Done",
    "status.STOPPED": "Stopped",
    "status.PROBLEM": "Problem",
    "status.WAITING_FOR_QA": "Waiting for QA",
    "status.QA_RUNNING": "QA running",
    "status.PACKING_RUNNING": "Packing",
    "status.PACKING": "Packing",
    "status.PACKED": "Packed/Completed",
    "status.COMPLETED": "Completed",
    "status.PRODUCED": "Produced",
    "status.DRAFT": "Draft",
    "status.QUEUED": "Queued",
    "status.PRINTING": "Printing",
    "status.CANCELLED": "Cancelled",
    "status.PRINTED": "Printed",
    "status.QA_REVIEW": "QA review",
    "status.QA_APPROVED": "QA approved",
    "status.LABEL_PRINTED": "Label printed",
    "status.SCRAP": "Scrap",
    "status.ACTIVE": "Active",
    "status.INACTIVE": "Inactive",
    "status.UNKNOWN": "Unknown",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(DEFAULT_LOCALE);

  useEffect(() => {
    const stored = window.localStorage.getItem("nordlicht.locale");
    if (SUPPORTED_LOCALES.includes(stored)) setLocaleState(stored);
  }, []);

  const setLocale = (nextLocale) => {
    const next = SUPPORTED_LOCALES.includes(nextLocale) ? nextLocale : DEFAULT_LOCALE;
    setLocaleState(next);
    window.localStorage.setItem("nordlicht.locale", next);
    document.documentElement.lang = next;
  };

  const value = useMemo(() => ({
    locale,
    setLocale,
    t(key, fallback = key) {
      return translations[locale]?.[key] ?? translations[DEFAULT_LOCALE]?.[key] ?? fallback;
    },
    status(value) {
      const key = String(value || "UNKNOWN").toUpperCase();
      return translations[locale]?.[`status.${key}`]
        ?? translations[DEFAULT_LOCALE]?.[`status.${key}`]
        ?? value
        ?? translations[locale]?.["status.UNKNOWN"];
    },
    formatDate(value, options) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-US", options).format(date);
    },
  }), [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error("useI18n must be used inside I18nProvider");
  return context;
}
